import { useQuery, UseQueryResult } from '@tanstack/react-query'
import { baseAppClient } from 'builder/modules/apiClient/apiClient'

type Params = {
  addon?: string
  fetchPrice?: boolean
}

type Payload = {
  addon_amount: number
  recurring_amount: number
  upsell_amount: number
  currency_symbol: string
}

type ResponsePayload = UseQueryResult<Payload, unknown> & {
  subscriptionAmount: () => string
  addonAmount: () => string
  upsellAmount: () => string
  recurringAmount: () => string
}

export const useGetAddonPrice = (params?: Params): ResponsePayload => {
  const getPlanApi = async () => {
    const res = await baseAppClient.get('/billing/plans.json', {
      params: {
        addon_name: params?.addon,
      },
    })
    return res.data
  }

  const res = useQuery<Payload>({
    queryKey: ['useGetAddonPrice', params],
    queryFn: getPlanApi,
    onError: (err: unknown) => console.error(err),
    useErrorBoundary: false,
    enabled: params?.fetchPrice,
  })

  const normalizeNumber = (amount?: number) => {
    if (!amount) return '0'
    return Number(amount / 100).toFixed(2)
  }

  const upsellAmount = () => {
    const amount = res.data?.upsell_amount
    const currency = res.data?.currency_symbol || '$'

    return `${currency}${normalizeNumber(amount)}`
  }

  const recurringAmount = () => {
    const amount = res.data?.recurring_amount
    const currency = res.data?.currency_symbol || '$'

    return `${currency}${normalizeNumber(amount)}`
  }

  const addonAmount = () => {
    const amount = res.data?.addon_amount
    const currency = res.data?.currency_symbol || '$'

    return `${currency}${normalizeNumber(amount)}`
  }

  const subscriptionAmount = () => {
    const recurringAmount = res.data?.recurring_amount || 0
    const addonAmount = res.data?.addon_amount || 0
    const amount = recurringAmount - addonAmount
    const currency = res.data?.currency_symbol || '$'

    return `${currency}${normalizeNumber(amount)}`
  }

  return { ...res, upsellAmount, recurringAmount, addonAmount, subscriptionAmount }
}
